import { useEffect, useState } from 'react';
import { companyHref, conferenceHref, leadHref, userHref } from '../components/entityHrefs';

/**
 * "Recently opened" records for the header search: the last few companies,
 * users, conferences and leads this operator opened, newest first.
 *
 * Per-viewer convenience only, so it lives in this browser's localStorage.
 * Every read and write is wrapped: storage can throw (private mode, blocked
 * site data) and the console must work the same without it.
 *
 * Detail pages call `useTrackRecent` once their record has loaded; the search
 * reads `useRecentItems`, which re-renders on every push (same tab, via a
 * window event) and on writes from other tabs (the `storage` event).
 */

export type RecentKind = 'company' | 'user' | 'conference' | 'lead';

export interface RecentItem {
  kind: RecentKind;
  id: string;
  /** What the operator recognises: company/user/conference name, lead preview. */
  label: string;
  /** Secondary line, e.g. a user's email or a lead's tenant name. */
  sublabel?: string;
}

const STORAGE_KEY = 'console.recent.v1';
export const RECENT_MAX = 10;
export const RECENT_EVENT = 'console:recent-changed';

const KINDS: readonly RecentKind[] = ['company', 'user', 'conference', 'lead'];

function isRecentItem(v: unknown): v is RecentItem {
  const r = v as RecentItem;
  return (
    !!r &&
    typeof r === 'object' &&
    KINDS.includes(r.kind) &&
    typeof r.id === 'string' &&
    typeof r.label === 'string' &&
    (r.sublabel === undefined || typeof r.sublabel === 'string')
  );
}

export function readRecent(): RecentItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isRecentItem).slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

/** Move `item` to the front (deduped on kind + id), capped at RECENT_MAX. */
export function pushRecent(item: RecentItem): void {
  const next = [
    item,
    ...readRecent().filter((r) => !(r.kind === item.kind && r.id === item.id)),
  ].slice(0, RECENT_MAX);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Best-effort: a storage failure only loses the recents list.
  }
  window.dispatchEvent(new Event(RECENT_EVENT));
}

export function clearRecent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Best-effort, as above.
  }
  window.dispatchEvent(new Event(RECENT_EVENT));
}

export function recentHref(item: Pick<RecentItem, 'kind' | 'id'>): string {
  switch (item.kind) {
    case 'company':
      return companyHref(item.id);
    case 'user':
      return userHref(item.id);
    case 'conference':
      return conferenceHref(item.id);
    case 'lead':
      return leadHref(item.id);
  }
}

/**
 * Record a detail page visit. Pass `null` until the record has loaded so a
 * not-found or still-loading page records nothing.
 */
export function useTrackRecent(item: RecentItem | null): void {
  const kind = item?.kind;
  const id = item?.id;
  const label = item?.label;
  const sublabel = item?.sublabel;
  useEffect(() => {
    if (kind && id && label) pushRecent({ kind, id, label, sublabel });
  }, [kind, id, label, sublabel]);
}

export function useRecentItems(): RecentItem[] {
  const [items, setItems] = useState<RecentItem[]>(() => readRecent());
  useEffect(() => {
    const refresh = () => setItems(readRecent());
    window.addEventListener(RECENT_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(RECENT_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);
  return items;
}
